import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Route, UrlSegment, Router, UrlTree } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ModeService } from '../services/mode.service';
import { AuthService, UserProfile } from '../services/auth.service';

/** Normalize path: strip query/fragment and trailing slash (except root). */
function normalizePath(raw: string): string {
  const clean = raw.split('?')[0]?.split('#')[0] ?? '/';
  if (!clean.startsWith('/')) return '/' + clean;
  return clean.endsWith('/') && clean !== '/' ? clean.slice(0, -1) : clean;
}

/** Centralized admin route detection. */
function isAdminRoute(path: string): boolean {
  return (
    path.startsWith('/user-management') ||
    path.startsWith('/create-user') ||
    path.startsWith('/product-management') ||
    path.startsWith('/category-management') ||
    path.startsWith('/memberships') ||
    path.startsWith('/admin/user/') ||
    path.startsWith('/groups') ||
    path.startsWith('/reports')
  );
}

/** Neutral/public routes are never constrained by UI mode. Extend as needed. */
function isNeutralOrPublic(path: string): boolean {
  return (
    path === '/' ||
    path === '/welcome' ||
    path.startsWith('/register') ||
    path.startsWith('/choose-memberships') ||
    path.startsWith('/checkout') || // /checkout-*, /subscription-success, etc.
    path.startsWith('/profile') || // profile/* allowed in both modes
    path.startsWith('/my-achievements') ||
    path.startsWith('/verify-email') ||
    path.startsWith('/auth/complete') ||
    path.startsWith('/set-password') ||
    path.startsWith('/course-catalog') ||
    path.startsWith('/course-completed') ||
    path.startsWith('/course-details') ||
    path.startsWith('/learning-path-details') ||
    path.startsWith('/school-signup')
  );
}

/** Role helpers. Adjust if your role strings differ. */
function roleOf(profile: UserProfile | null): string {
  return (profile?.role ?? '').toString().trim().toLowerCase();
}
function isSuperAdmin(profile: UserProfile | null): boolean {
  return roleOf(profile) === 'superadmin';
}
function isSupportAdmin(profile: UserProfile | null): boolean {
  return roleOf(profile) === 'supportadmin';
}

/**
 * VERY SMALL FUSE to avoid infinite redirect ping-pong if something goes wrong in prod.
 * Resets every 2 seconds. If more than 12 guard decisions happen in that window, we stop redirecting.
 */
let _guardDecisions = 0;
setInterval(() => (_guardDecisions = 0), 2000);

/**
 * UX steering:
 * - Waits for auth readiness to avoid redirecting on a stale/null profile.
 * - SuperAdmin/SupportAdmin: ADMIN-ONLY UX (dashboard & other learner pages are not accessible).
 * - Everyone else: enforce UI-mode boundaries.
 * - Neutral/public routes are never constrained by UI mode.
 * NOTE: AuthGuard/PolicyGuard still decide authentication/permissions; this only steers UX routing.
 */
function guardCheck$(targetPath: string): Observable<true | UrlTree> {
  const router = inject(Router);
  const mode = inject(ModeService);
  const auth = inject(AuthService);

  const path = normalizePath(targetPath);

  // Public/neutral: do not constrain by UI mode.
  if (isNeutralOrPublic(path)) return of(true);

  return combineLatest([auth.authReady$, auth.isAuthenticated$, auth.userProfile$]).pipe(
    filter(([ready]) => !!ready),
    take(1),
    map(([_, __, profile]) => {
      // Fuse: if guard fires too often in a short time, stop redirecting to break any loop.
      if (++_guardDecisions > 12) {
        return true as const;
      }

      const adminTarget = isAdminRoute(path);
      const learnerTarget = !adminTarget;

      // IMPORTANT REQUIREMENT:
      // SuperAdmin and SupportAdmin must NOT have Dashboard (learner area) as home or accessible view.
      // Any attempt to navigate to learner pages should redirect to /user-management.
      if (isSuperAdmin(profile) || isSupportAdmin(profile)) {
        if (learnerTarget) {
          const to = '/user-management';
          return path === to ? (true as const) : router.createUrlTree([to]);
        }
        return true as const;
      }

      // Enforce UI mode for everyone else
      if (mode.isLearner() && adminTarget) {
        const to = '/dashboard';
        return path === to ? (true as const) : router.createUrlTree([to]);
      }

      if (mode.isAdmin() && learnerTarget) {
        const to = '/user-management';
        return path === to ? (true as const) : router.createUrlTree([to]);
      }

      return true as const;
    }),
  );
}

/** Use as canActivate on component routes (good for non-lazy routes). */
export const UiModeGuard: CanActivateFn = (_route, state) => guardCheck$(state.url || '/');

/** Use as canMatch on (lazy) routes (prevents loading module if not matching UI mode). */
export const UiModeCanMatch: CanMatchFn = (_route: Route, segments: UrlSegment[]) => {
  const path = '/' + segments.map((s) => s.path).join('/');
  return guardCheck$(path);
};
